
import * as uuid from 'uuid';
import logger from '../common/logger';
import * as testPurchase from '../common/testPurchaseData.json';
import { formatPhoneNumber, getInputValueById } from '../common/utils';
import COUNTRY_PHONE_CODES from '../data/country-phone-codes';
import TrackingManager from '../guCommonTracking';
import {getAltDomain} from '../utils/urls'

const FORM_ELEMENT_IDS = {
  email: 'form-field-shipping_info_email',
  first_name: 'form-field-shipping_info_fname',
  last_name: 'form-field-shipping_info_lname',
  phone_number: 'form-field-shipping_info_phone',
  city: 'form-field-shipping_info_city',
  region: 'form-field-shipping_info_state',
  postal_code: 'form-field-shipping_info_zip',
  country: 'form-field-shipping_info_country'
}

const getDomain = () => {
  const altDomain = getAltDomain();
  if (altDomain) {
    return altDomain;
  }

  if (typeof window.getDomain === 'function') {
    return window.getDomain();
  }

  console.error('window.getDomain is not defined or is not a function');
  return null;
};

const EventDataService = {
  populateOfferGuParams: () => {
    try {
      if (!window.gu_qs) { window.gu_qs = {}; }
      if (window.gu_qs?.gutestpurchase === '1') {
        window.gu_qs.externalId = testPurchase.fakeRequestId
      } else {
        window.gu_qs.externalId = window?.gu_qs?.req_id ?? uuid.v4();
      }
      window.gu_qs['br'] = window.brand_name;
      window.gu_qs['imor'] = window.is_mor;
    } catch (e) {
      logger.error('populateOfferGuParams Failed', e, { module });
    }
  },
  getEmailAddress: (order) => {
    try {
      const elementId = FORM_ELEMENT_IDS.email;
      return order?.email || order?.shipping_address?.email || order?.billing_address?.email || getInputValueById(elementId);
    } catch (e) {
      return null;
    }
  },
  getFirstName: (order) => {
    try {
      const elementId = FORM_ELEMENT_IDS.first_name;
      return order?.shipping_address?.first_name || order?.billing_address?.first_name || getInputValueById(elementId);
    } catch (e) {
      return null;
    }
  },
  getLastName: (order) => {
    try {
      const elementId = FORM_ELEMENT_IDS.last_name;
      return order?.shipping_address?.last_name || order?.billing_address?.last_name || getInputValueById(elementId);
    } catch (e) {
      return null;
    }
  },
  getPhoneNumber: (order) => {
    try {
      const elementId = FORM_ELEMENT_IDS.phone_number;
      return order?.phone || order?.shipping_address?.phone || order?.billing_address?.phone || getInputValueById(elementId);
    } catch (e) {
      return null;
    }
  },
  getCity: (order) => {
    try {
      const elementId = FORM_ELEMENT_IDS.city;
      return order?.billing_address?.city || getInputValueById(elementId) || window?.gu_qs?.city;
    } catch (e) {
      return null;
    }
  },
  getRegion: (order) => {
    try {
      return order?.billing_address?.province_code || window?.gu_qs?.regionCode;
    } catch (e) {
      return null;
    }
  },
  getPostalCode: (order) => {
    try {
      const elementId = FORM_ELEMENT_IDS.postal_code;
      return order?.billing_address?.zip || getInputValueById(elementId) || window?.gu_qs?.postalCode;
    } catch (e) {
      return null;
    }
  },
  getCountryCode: (order) => {
    try {
      const elementId = FORM_ELEMENT_IDS.country;
      const localStorageCountry = window?.localStorage.getItem('country');
      const countryCode = order?.billing_address?.country_code || getInputValueById(elementId) ||window?.gu_qs?.gucountry || localStorageCountry;
      return countryCode.toUpperCase();
    } catch (e) {
      return null;
    }
  },
  populateGoogleEnhancedConversionData: (propertyMeta) => {
    try {
      const order = propertyMeta;
      let enhancedConversionData = {
        email: EventDataService.getEmailAddress(order),
        unformatted_phone_number: EventDataService.getPhoneNumber(order),
        first_name: EventDataService.getFirstName(order),
        last_name: EventDataService.getLastName(order),
        home_address: {
          city: EventDataService.getCity(order),
          region: EventDataService.getRegion(order),
          postal_code: EventDataService.getPostalCode(order),
          country: EventDataService.getCountryCode(order),
        }
      };

      if (enhancedConversionData?.unformatted_phone_number && enhancedConversionData?.home_address?.country
        && COUNTRY_PHONE_CODES?.[enhancedConversionData?.home_address?.country]) {
        const extensionCode = COUNTRY_PHONE_CODES?.[enhancedConversionData?.home_address?.country];
        const formattedPhoneNumber = formatPhoneNumber(enhancedConversionData?.unformatted_phone_number, extensionCode);
        enhancedConversionData.formatted_phone_number = formattedPhoneNumber;
        enhancedConversionData.phone_number = formattedPhoneNumber;
      }
      return enhancedConversionData;
    } catch (e) {
      logger.error('populateEnhancedConversionProperties Failed', e, { module });
    }
  },
  buildEventProperties: (eventName, action, identifier, propertyName, propertyValue, pagePath, propertyMeta) => {
    try {
      let eventProperties = { 'action': action, 'identifier': identifier };
      for (const e in window.gu_qs) {
        eventProperties[e] = window.gu_qs[e]
      }
      eventProperties['gu_correlationId'] = window.gu_correlationId || uuid.v4();
      eventProperties['url'] = document.baseURI;
      eventProperties['enterpriseUrl'] = `https://${TrackingManager.config?.gu_offer_url}${document.location.pathname.replace(/\/$/, '')}`;
      eventProperties['title'] = document.title;
      eventProperties['offer'] = TrackingManager.config?.gu_offer_url ?? window.getDomain(); //lpDomain
      eventProperties['domain'] = getDomain();
      eventProperties['timeZone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
      //eventProperties['page'] = document.location.pathname;
      eventProperties['offerPage'] = document.location.pathname;//constant throught shopify
      eventProperties['mobileDevice'] = window.mobileDevice;
      eventProperties['pageType'] = 'Landing Page'; //preSellPage
      eventProperties['eventName'] = eventName;
      eventProperties['propertyName'] = propertyName;
      eventProperties['propertyValue'] = propertyValue;
      eventProperties['pagePath'] = pagePath;
      eventProperties['propertyMeta'] = propertyMeta;
      if (!eventProperties['referringSite']) {
        eventProperties['referringSite'] = document.referrer;
      }
      if (!eventProperties['landingSite']) {
        eventProperties['landingSite'] = window.location.href;
      }
      if (!eventProperties['gu_qs']) {
        eventProperties['gu_qs'] = JSON.stringify(window.gu_qs);
      }
      if (window?.gu_qs?.req_id) {
        eventProperties['eventId'] = `${window?.gu_qs?.req_id}-${uuid.v4()}`;
      } else {
        eventProperties['eventId'] = uuid.v4();
      }

      if (['Order Conversion', 'Shipping Info'].includes(eventName)) {
        const order = propertyMeta;

        eventProperties['brand'] = '{{Offer - Brand Name}}';
        eventProperties['req_id'] = window?.gu_qs?.req_id;
        eventProperties['email'] = order?.email || order?.shipping_address?.email || order?.billing_address?.email;
        eventProperties['phone'] = order?.phone || order?.shipping_address?.phone || order?.billing_address?.phone;
        eventProperties['firstName'] = order?.billing_address?.first_name;
        eventProperties['lastName'] = order?.billing_address?.last_name;
        EventDataService.populateOfferGuParams();
      }
      eventProperties.enhancedConversionData = EventDataService.populateGoogleEnhancedConversionData(propertyMeta);
      eventProperties.fb_medical = TrackingManager.config?.fb_medical;

      eventProperties['ipAddress'] = window?.cachedIpAddress;

      return eventProperties;
    } catch (e) {
      logger.error('buildEventProperties Failed', e, { module });
    }
  },
  buildTestEventProperties: () => {
    let eventProperties = { 'action': 'FAKE PURCHASE', 'identifier': 'FAKE' };

    for (const e in window.gu_qs) {
      eventProperties[e] = window.gu_qs[e];
    }
    eventProperties['gu_correlationId'] = window?.gu_correlationId || uuid.v4();
    eventProperties['url'] = document.baseURI;
    eventProperties['enterpriseUrl'] = `https://${TrackingManager.config?.gu_offer_url}${document.location.pathname.replace(/\/$/, '')}`;
    eventProperties['title'] = document.title;
    eventProperties['offer'] = TrackingManager.config?.gu_offer_url ?? window.getDomain(); //lpDomain
    eventProperties['domain'] = window.getDomain();
    eventProperties['timeZone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
    eventProperties['offerPage'] = document.location.pathname;
    eventProperties['mobileDevice'] = window.mobileDevice;
    eventProperties['pageType'] = 'Landing Page';
    eventProperties['eventName'] = 'Order Conversion';
    eventProperties['referringSite'] = testPurchase.fakeReferrer;
    if (!eventProperties['gu_qs']) {
      eventProperties['gu_qs'] = JSON.stringify(window.gu_qs);
    }
    eventProperties['externalId'] = testPurchase.fakeRequestId;
    eventProperties['eventId'] = testPurchase.fakeRequestId;
    eventProperties['brand'] = '{{Offer - Brand Name}}';
    window.gu_payments = testPurchase.fakeOrder;
    EventDataService.populateOfferGuParams();
    eventProperties.enhancedConversionData = EventDataService.populateGoogleEnhancedConversionData({});
    eventProperties['ipAddress'] = window?.cachedIpAddress;

    return eventProperties;
  },
};
export default EventDataService;